import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from "js-cookie";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Plus,
    Star,
    Bookmark,
    Archive,
    ArchiveRestore,
    ChevronDown,
    ChevronRight,
    MoreVertical,
    FolderPlus,
    Hash,
    StarOff,
    BookmarkMinus,
    User
} from 'lucide-react';

import { BACKEND_API_URL } from "../utils/api";
import { setAllChannels, updateChannel, setChannelPanelOpen } from "../redux/actions/channel.action";
import { selectChannels, selectAllChannels } from "../redux/selectors/channel.selectors";
import { selectUser } from "../redux/selectors/canvas.selectors";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

// Helper function to get compact relative time (WhatsApp-style)
const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const messageDate = new Date(timestamp);
    const diffMs = now - messageDate;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffSecs < 60) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    if (diffWeeks < 4) return `${diffWeeks}w`;
    
    // For older messages, show date
    const options = { month: 'short', day: 'numeric' };
    if (messageDate.getFullYear() !== now.getFullYear()) {
        options.year = '2-digit';
    }
    return messageDate.toLocaleDateString('en-US', options);
};

const ChannelList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { channelId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedChannel = channelId ? parseInt(channelId) : null;

    const channels = useSelector(selectChannels);
    const allChannels = useSelector(selectAllChannels);
    const currentUser = useSelector(selectUser);

    const [newChannelName, setNewChannelName] = useState('');
    const [newChannelDescription, setNewChannelDescription] = useState('');
    const [createChannelOpen, setCreateChannelOpen] = useState(false);
    const [editingChannelId, setEditingChannelId] = useState(null);
    const [editingChannelName, setEditingChannelName] = useState('');
    const [customGroups, setCustomGroups] = useState([]);
    const [newGroupName, setNewGroupName] = useState('');
    const [createGroupOpen, setCreateGroupOpen] = useState(false);
    const [selectedGroupForChannel, setSelectedGroupForChannel] = useState(null);
    const [renameGroupDialogOpen, setRenameGroupDialogOpen] = useState(false);
    const [renamingGroupId, setRenamingGroupId] = useState(null);
    const [renamingGroupName, setRenamingGroupName] = useState('');

    // Get activeTab from URL, default to 'all'
    const activeTab = searchParams.get('tab') || 'all';

    // Function to update the active tab and URL
    const handleTabChange = (newTab) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (newTab === 'all') {
            newSearchParams.delete('tab');
        } else {
            newSearchParams.set('tab', newTab);
        }
        setSearchParams(newSearchParams, { replace: true });
    };

    const [collapsedGroups, setCollapsedGroups] = useState(() => {
        const saved = localStorage.getItem('channelGroupsCollapsed');
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        localStorage.setItem('channelGroupsCollapsed', JSON.stringify(collapsedGroups));
    }, [collapsedGroups]);

    useEffect(() => {
        if (activeTab === 'starred' || activeTab === 'bookmarked' || activeTab === 'personalized' || activeTab === 'archived') {
            const allGroupKeys = [
                ...customGroups.map(g => `custom_${g.id}`),
                'regular'
            ];
            const expandedState = {};
            allGroupKeys.forEach(key => {
                expandedState[key] = false;
            });
            setCollapsedGroups(prev => ({
                ...prev,
                ...expandedState
            }));
        }
    }, [activeTab, customGroups]);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const accessToken = Cookies.get('access_token');
                const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/channel-groups/`, {
                    headers: { Authorization: `Token ${accessToken}` },
                });
                const groups = Array.isArray(response.data) ? response.data : [];
                // Backend already sorts by latest_activity (recentness), then by order, then by name
                // Only ensure __PERSONALISED group stays first
                groups.sort((a, b) => {
                    if (a.name === '__PERSONALISED') return -1;
                    if (b.name === '__PERSONALISED') return 1;
                    // Preserve backend order (sorted by recentness) for all other groups
                    return 0;
                });
                setCustomGroups(groups);
            } catch (error) {
                console.error('Failed to fetch groups:', error);
            }
        };
        fetchGroups();
    }, []);

    const toggleGroup = (groupKey) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const filteredChannels = useMemo(() => {
        let result;
        if (activeTab === 'starred') {
            result = channels.filter(ch => ch.is_favorited);
        } else if (activeTab === 'bookmarked') {
            result = channels.filter(ch => ch.is_bookmarked);
        } else if (activeTab === 'personalized') {
            // Backend handles sorting (user's own channel first, then by latest message time)
            result = channels.filter(ch => ch.is_personalized);
        } else if (activeTab === 'archived') {
            // Use allChannels for archived tab since channels already excludes archived
            result = allChannels.filter(ch => ch.is_archived);
        } else {
            // For 'all' tab, exclude personalized channels and archived channels
            result = channels.filter(ch => !ch.is_personalized && !ch.is_archived);
        }
        return result;
    }, [channels, allChannels, activeTab]);

    const groupedChannels = useMemo(() => {
        const groups = {};

        const safeCustomGroups = Array.isArray(customGroups) ? customGroups : [];

        // Include __PERSONALISED group in personalized, starred, and bookmarked tabs
        const shouldShowPersonalisedGroup = activeTab === 'personalized' || activeTab === 'starred' || activeTab === 'bookmarked';

        safeCustomGroups.forEach(group => {
            // Skip __PERSONALISED group in 'all' tab
            if (group.name === '__PERSONALISED' && !shouldShowPersonalisedGroup) {
                return;
            }
            groups[`custom_${group.id}`] = {
                type: 'custom',
                id: group.id,
                name: group.name,
                color: group.color,
                channels: []
            };
        });

        groups['regular'] = { type: 'default', name: 'Other Channels', channels: [] };

        filteredChannels.forEach(channel => {
            // If channel is personalized, put it in __PERSONALISED group
            if (channel.is_personalized) {
                const personalisedGroup = safeCustomGroups.find(g => g.name === '__PERSONALISED');
                if (personalisedGroup && shouldShowPersonalisedGroup) {
                    const groupKey = `custom_${personalisedGroup.id}`;
                    if (!groups[groupKey]) {
                        groups[groupKey] = {
                            type: 'custom',
                            id: personalisedGroup.id,
                            name: personalisedGroup.name,
                            color: personalisedGroup.color,
                            channels: []
                        };
                    }
                    groups[groupKey].channels.push(channel);
                    return;
                }
            }

            // Regular grouping logic for non-personalized channels
            if (channel.group_id && channel.group_id !== null && channel.group_id !== undefined) {
                const groupKey = `custom_${channel.group_id}`;
                if (groups[groupKey]) {
                    groups[groupKey].channels.push(channel);
                } else {
                    groups['regular'].channels.push(channel);
                }
            } else {
                groups['regular'].channels.push(channel);
            }
        });

        // Backend handles sorting (user's own channel first, then by latest message time, then by id)
        // Channels are already sorted from the API response, so we preserve that order

        return groups;
    }, [filteredChannels, customGroups, activeTab]);

    const handleChannelSelect = (id) => {
        const newSearchParams = new URLSearchParams(searchParams);
        // Preserve tab parameter if it's not 'all'
        const tabParam = newSearchParams.get('tab');
        if (!tabParam || tabParam === 'all') {
            newSearchParams.delete('tab');
        }
        const queryString = newSearchParams.toString();
        const url = queryString ? `/channels/${id}?${queryString}` : `/channels/${id}`;
        navigate(url);
        dispatch(setChannelPanelOpen(false));
    };

    const createNewChannel = async () => {
        if (!newChannelName.trim()) {
            toast.error('Channel name is required');
            return;
        }

        const formData = new FormData();
        formData.append('name', newChannelName);
        formData.append('description', newChannelDescription);
        if (selectedGroupForChannel) {
            formData.append('group_id', selectedGroupForChannel);
        }

        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/channels/`, formData, {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            const newChannel = response.data;
            if (selectedGroupForChannel && !newChannel.group_id) {
                const group = customGroups.find(g => g.id === selectedGroupForChannel);
                if (group) {
                    newChannel.group_id = group.id;
                    newChannel.group_name = group.name;
                }
            }

            dispatch(setAllChannels([...allChannels, newChannel]));
            setCreateChannelOpen(false);
            setNewChannelName('');
            setNewChannelDescription('');
            setSelectedGroupForChannel(null);
            toast.success('Channel created successfully!');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to create channel.');
        }
    };

    const createNewGroup = async () => {
        if (!newGroupName.trim()) {
            toast.error('Group name cannot be empty');
            return;
        }

        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/channel-groups/`, {
                name: newGroupName,
                color: '#808080'
            }, {
                headers: { Authorization: `Token ${accessToken}` },
            });

            setCustomGroups([...customGroups, response.data]);
            setNewGroupName('');
            setCreateGroupOpen(false);
            toast.success('Group created successfully!');
        } catch (error) {
            const errorText = error?.response?.data?.error || 'Failed to create group';
            toast.error(errorText.includes('already exists') ? 'Group with same name exists' : errorText);
        }
    };

    const deleteGroup = async (groupId) => {
        try {
            const accessToken = Cookies.get('access_token');
            await axios.delete(`${REACT_APP_BACKEND_API_URL}/api/channel-groups/${groupId}/`, {
                headers: { Authorization: `Token ${accessToken}` },
            });
            setCustomGroups(customGroups.filter(g => g.id !== groupId));
            toast.success('Group deleted successfully!');
        } catch (error) {
            toast.error('Failed to delete group');
        }
    };

    const updateGroupName = async () => {
        if (!renamingGroupName.trim()) {
            toast.error('Group name cannot be empty');
            return;
        }

        const group = customGroups.find(g => g.id === renamingGroupId);
        if (group && group.name === renamingGroupName) {
            setRenameGroupDialogOpen(false);
            return;
        }

        try {
            const accessToken = Cookies.get('access_token');
            await axios.patch(`${REACT_APP_BACKEND_API_URL}/api/channel-groups/${renamingGroupId}/`, {
                name: renamingGroupName
            }, {
                headers: { Authorization: `Token ${accessToken}` },
            });

            setCustomGroups(customGroups.map(g =>
                g.id === renamingGroupId ? { ...g, name: renamingGroupName } : g
            ));
            setRenameGroupDialogOpen(false);
            setRenamingGroupId(null);
            setRenamingGroupName('');
            toast.success('Group renamed successfully!');
        } catch (error) {
            toast.error('Failed to rename group');
        }
    };

    const moveChannelToGroup = async (channelId, groupId) => {
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/move_to_group/`, {
                group_id: groupId
            }, {
                headers: { Authorization: `Token ${accessToken}` },
            });

            dispatch(updateChannel(channelId, {
                group_id: response.data.group_id,
                group_name: response.data.group_name
            }));
            toast.success('Channel moved successfully!');
        } catch (error) {
            toast.error('Failed to move channel');
        }
    };

    const updateChannelName = async (channelId) => {
        if (!editingChannelName.trim()) {
            toast.error('Channel name cannot be empty');
            setEditingChannelId(null);
            return;
        }

        const channel = channels.find(c => c.id === channelId);
        if (channel.name === editingChannelName) {
            setEditingChannelId(null);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('name', editingChannelName);
            const accessToken = Cookies.get('access_token');

            await axios.patch(`${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/`, formData, {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            dispatch(updateChannel(channelId, { name: editingChannelName }));
            setEditingChannelId(null);
            toast.success('Channel renamed successfully!');
        } catch (error) {
            toast.error('Failed to rename channel');
            setEditingChannelId(null);
        }
    };

    const toggleFavorite = async (channelId) => {
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/toggle_favorite/`, {}, {
                headers: { Authorization: `Token ${accessToken}` },
            });
            dispatch(updateChannel(channelId, { is_favorited: response.data.is_favorited }));
            toast.success(response.data.is_favorited ? 'Channel starred!' : 'Channel unstarred');
        } catch (error) {
            toast.error('Failed to update favorite status');
        }
    };

    const toggleBookmark = async (channelId) => {
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/toggle_bookmark/`, {}, {
                headers: { Authorization: `Token ${accessToken}` },
            });
            dispatch(updateChannel(channelId, { is_bookmarked: response.data.is_bookmarked }));
            toast.success(response.data.is_bookmarked ? 'Channel bookmarked!' : 'Channel unbookmarked');
        } catch (error) {
            toast.error('Failed to update bookmark status');
        }
    };

    const toggleArchive = async (channelId) => {
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.post(`${REACT_APP_BACKEND_API_URL}/api/channels/${channelId}/toggle_archive/`, {}, {
                headers: { Authorization: `Token ${accessToken}` },
            });

            dispatch(updateChannel(channelId, { is_archived: response.data.is_archived }));

            if (selectedChannel === channelId && response.data.is_archived) {
                const newSearchParams = new URLSearchParams(searchParams);
                const tabParam = newSearchParams.get('tab');
                const url = tabParam && tabParam !== 'all'
                    ? `/channels?tab=${tabParam}`
                    : '/channels';
                navigate(url);
            }

            toast.success(response.data.is_archived ? 'Channel archived!' : 'Channel unarchived');
        } catch (error) {
            toast.error('Failed to update archive status');
        }
    };

    // Channel Item Component - WhatsApp-style two-line layout
    const ChannelItem = ({ channel }) => {
        const isSelected = selectedChannel === channel.id;
        const isBookmarkTab = activeTab === 'bookmarked';
        const isPersonalized = channel.is_personalized;
        const isOwnChannel = isPersonalized && currentUser && String(channel.created_by) === String(currentUser.user_id);
        const [imageError, setImageError] = useState(false);
        const latestMessage = channel.latest_message;

        const showProfilePicture = isPersonalized && channel.creator_picture && !imageError;
        const showHashIcon = !isPersonalized || !channel.creator_picture || imageError;

        // Build the message preview text
        const getMessagePreview = () => {
            if (!latestMessage) return null;
            
            const authorPrefix = latestMessage.author_name ? `${latestMessage.author_name}: ` : '';
            const messageText = latestMessage.message || '';
            
            return { authorPrefix, messageText };
        };

        const preview = getMessagePreview();

        return (
            <div
                className={cn(
                    "group flex gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150",
                    "hover:bg-accent",
                    isSelected && "bg-primary text-primary-foreground hover:bg-primary/90",
                    channel.is_archived && "opacity-50"
                )}
                onClick={() => editingChannelId !== channel.id && handleChannelSelect(channel.id)}
                onDoubleClick={() => {
                    if (!isBookmarkTab && !isPersonalized) {
                        setEditingChannelId(channel.id);
                        setEditingChannelName(channel.name);
                    }
                }}
            >
                {/* Avatar/Icon Column */}
                <div className="shrink-0 pt-0.5">
                    {showProfilePicture ? (
                        <img
                            src={channel.creator_picture}
                            alt={channel.name}
                            className={cn(
                                "h-10 w-10 rounded-full object-cover",
                                isSelected && "ring-2 ring-primary-foreground/30"
                            )}
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className={cn(
                            "h-10 w-10 rounded-full flex items-center justify-center",
                            isSelected ? "bg-primary-foreground/20" : "bg-muted"
                        )}>
                            <Hash className={cn(
                                "h-5 w-5",
                                isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
                            )} />
                        </div>
                    )}
                </div>

                {/* Content Column */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                    {editingChannelId === channel.id && !isBookmarkTab && !isPersonalized ? (
                        <Input
                            value={editingChannelName}
                            onChange={(e) => setEditingChannelName(e.target.value)}
                            onBlur={() => updateChannelName(channel.id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') updateChannelName(channel.id);
                                if (e.key === 'Escape') setEditingChannelId(null);
                            }}
                            autoFocus
                            className="h-7 text-sm"
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <>
                            {/* First Row: Channel name + indicators + time */}
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                    <span className="text-sm font-medium truncate">
                                        {channel.name}
                                        {isOwnChannel && (
                                            <span className={cn(
                                                "ml-1 text-xs font-normal",
                                                isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
                                            )}>(you)</span>
                                        )}
                                    </span>
                                    {/* Status indicators */}
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        {channel.is_favorited && (
                                            <Star className={cn(
                                                "h-3 w-3",
                                                isSelected ? "text-yellow-300" : "text-yellow-500"
                                            )} fill="currentColor" />
                                        )}
                                        {channel.is_bookmarked && (
                                            <Bookmark className={cn(
                                                "h-3 w-3",
                                                isSelected ? "text-blue-300" : "text-blue-500"
                                            )} fill="currentColor" />
                                        )}
                                        {channel.is_archived && (
                                            <Archive className="h-3 w-3" />
                                        )}
                                    </div>
                                </div>
                                
                                {/* Time + Menu */}
                                <div className="flex items-center gap-1 shrink-0">
                                    {latestMessage?.created_at && (
                                        <span className={cn(
                                            "text-[11px] tabular-nums",
                                            isSelected ? "text-primary-foreground/60" : "text-muted-foreground"
                                        )}>
                                            {getRelativeTime(latestMessage.created_at)}
                                        </span>
                                    )}
                                    
                                    {!isBookmarkTab && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className={cn(
                                                        "h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity",
                                                        isSelected && "hover:bg-primary-foreground/10"
                                                    )}
                                                >
                                                    <MoreVertical className="h-3.5 w-3.5" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48">
                                                {!isPersonalized && (
                                                    <DropdownMenuItem onClick={() => toggleArchive(channel.id)}>
                                                        {channel.is_archived ? (
                                                            <><ArchiveRestore className="h-4 w-4 mr-2" /> Unarchive</>
                                                        ) : (
                                                            <><Archive className="h-4 w-4 mr-2" /> Archive</>
                                                        )}
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuItem onClick={() => toggleFavorite(channel.id)}>
                                                    {channel.is_favorited ? (
                                                        <><StarOff className="h-4 w-4 mr-2" /> Unstar</>
                                                    ) : (
                                                        <><Star className="h-4 w-4 mr-2" /> Star</>
                                                    )}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => toggleBookmark(channel.id)}>
                                                    {channel.is_bookmarked ? (
                                                        <><BookmarkMinus className="h-4 w-4 mr-2" /> Unbookmark</>
                                                    ) : (
                                                        <><Bookmark className="h-4 w-4 mr-2" /> Bookmark</>
                                                    )}
                                                </DropdownMenuItem>
                                                {!isPersonalized && (
                                                    <>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuSub>
                                                            <DropdownMenuSubTrigger>
                                                                <FolderPlus className="h-4 w-4 mr-2" /> Move to group
                                                            </DropdownMenuSubTrigger>
                                                            <DropdownMenuSubContent>
                                                                {customGroups
                                                                    .filter(group => group.name !== '__PERSONALISED')
                                                                    .map(group => (
                                                                        <DropdownMenuItem
                                                                            key={group.id}
                                                                            onClick={() => moveChannelToGroup(channel.id, group.id)}
                                                                        >
                                                                            {group.name}
                                                                        </DropdownMenuItem>
                                                                    ))}
                                                                <DropdownMenuSeparator />
                                                                <DropdownMenuItem onClick={() => moveChannelToGroup(channel.id, null)}>
                                                                    No Group
                                                                </DropdownMenuItem>
                                                            </DropdownMenuSubContent>
                                                        </DropdownMenuSub>
                                                    </>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </div>
                            </div>

                            {/* Second Row: Message preview */}
                            <div className={cn(
                                "text-xs truncate mt-0.5 leading-relaxed",
                                isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
                            )}>
                                {preview ? (
                                    <>
                                        <span className={cn(
                                            "font-medium",
                                            isSelected ? "text-primary-foreground/80" : "text-foreground/70"
                                        )}>
                                            {preview.authorPrefix}
                                        </span>
                                        {preview.messageText}
                                    </>
                                ) : (
                                    <span className="italic opacity-60">No messages yet</span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // Group Header Component
    const GroupHeader = ({ groupKey, group }) => {
        const isCollapsed = collapsedGroups[groupKey] || false;
        const isCustom = group.type === 'custom';
        const isBookmarkTab = activeTab === 'bookmarked';
        const isPersonalisedGroup = group.name === '__PERSONALISED';

        return (
            <Collapsible open={!isCollapsed} onOpenChange={() => toggleGroup(groupKey)}>
                <div className={cn(
                    "flex items-center justify-between px-2 py-1.5 rounded-md mb-1",
                    isCustom ? "bg-muted/50" : "bg-muted/30"
                )}>
                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 hover:bg-transparent">
                            {isCollapsed ? (
                                <ChevronRight className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                            {isPersonalisedGroup && (
                                <User className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                            <span className="font-semibold text-sm">
                                {group.name}
                            </span>
                            <span className="text-xs text-muted-foreground">({group.channels.length})</span>
                        </Button>
                    </CollapsibleTrigger>

                    {isCustom && !isBookmarkTab && !isPersonalisedGroup && (
                        <div className="flex items-center gap-1">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedGroupForChannel(group.id);
                                                setCreateChannelOpen(true);
                                            }}
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Add channel to group</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-6 w-6">
                                        <MoreVertical className="h-3.5 w-3.5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => {
                                        setRenamingGroupId(group.id);
                                        setRenamingGroupName(group.name);
                                        setRenameGroupDialogOpen(true);
                                    }}>
                                        Rename
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="text-destructive"
                                        onClick={() => {
                                            if (window.confirm(`Delete group "${group.name}"? Channels will be moved to "Other Channels".`)) {
                                                deleteGroup(group.id);
                                            }
                                        }}
                                    >
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>

                <CollapsibleContent className="pl-1 space-y-0.5">
                    {group.channels.map(channel => (
                        <ChannelItem key={channel.id} channel={channel} />
                    ))}
                </CollapsibleContent>
            </Collapsible>
        );
    };

    return (
        <div className="h-full flex flex-col">
            {/* Tabs */}
            <div className="px-2 pt-2 pb-1 border-b shrink-0">
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                    <TabsList className="w-full grid grid-cols-5">
                        <TabsTrigger value="all" className="text-xs gap-1 cursor-pointer" title="All channels">
                            <Hash className="h-3 w-3" />
                        </TabsTrigger>
                        <TabsTrigger value="starred" className="text-xs gap-1 cursor-pointer" title="Starred channels">
                            <Star className="h-3 w-3" />
                        </TabsTrigger>
                        <TabsTrigger value="bookmarked" className="text-xs gap-1 cursor-pointer" title="Bookmarked channels">
                            <Bookmark className="h-3 w-3" />
                        </TabsTrigger>
                        <TabsTrigger value="personalized" className="text-xs gap-1 cursor-pointer" title="Personal channels">
                            <User className="h-3 w-3" />
                        </TabsTrigger>
                        <TabsTrigger value="archived" className="text-xs gap-1 cursor-pointer" title="Archived channels">
                            <Archive className="h-3 w-3" />
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {/* Channel List */}
            <ScrollArea className="flex-1 px-2">
                <div className="py-2 space-y-1">
                    {activeTab === 'personalized' || activeTab === 'archived' ? (
                        // In personalized and archived tabs, show channels directly without group headers
                        filteredChannels.map(channel => (
                            <ChannelItem key={channel.id} channel={channel} />
                        ))
                    ) : (
                        <>
                            {customGroups
                                .filter(group => {
                                    // Show __PERSONALISED group in starred and bookmarked tabs
                                    // Hide it only in 'all' tab
                                    if (group.name === '__PERSONALISED' && activeTab === 'all') {
                                        return false;
                                    }
                                    return true;
                                })
                                .map(group => {
                                    const groupKey = `custom_${group.id}`;
                                    const groupData = groupedChannels[groupKey] || {
                                        type: 'custom',
                                        id: group.id,
                                        name: group.name,
                                        color: group.color,
                                        channels: []
                                    };
                                    // Only show groups that have at least one channel
                                    if (groupData.channels.length === 0) {
                                        return null;
                                    }
                                    return <GroupHeader key={groupKey} groupKey={groupKey} group={groupData} />;
                                })}

                            {/* Only show "Other Channels" group if it has channels */}
                            {groupedChannels['regular']?.channels.length > 0 && (
                                <GroupHeader groupKey="regular" group={groupedChannels['regular']} />
                            )}
                        </>
                    )}
                </div>
            </ScrollArea>

            {/* Create Buttons - Hidden in bookmarks and personalized tabs */}
            {activeTab !== 'bookmarked' && activeTab !== 'personalized' && activeTab !== 'archived' && (
                <div className="p-2 border-t space-y-2 shrink-0">
                    <Button
                        variant="outline"
                        className="w-full justify-start gap-2"
                        onClick={() => setCreateChannelOpen(true)}
                    >
                        <Hash className="h-4 w-4" />
                        New Channel
                        <Plus className="h-4 w-4 ml-auto" />
                    </Button>
                    <Button
                        variant="outline"
                        className="w-full justify-start gap-2"
                        onClick={() => setCreateGroupOpen(true)}
                    >
                        <FolderPlus className="h-4 w-4" />
                        New Group
                        <Plus className="h-4 w-4 ml-auto" />
                    </Button>
                </div>
            )}

            {/* Create Channel Dialog */}
            <Dialog open={createChannelOpen} onOpenChange={setCreateChannelOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New Channel</DialogTitle>
                        <DialogDescription>
                            Add a new channel to organize your conversations.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="channel-name">Channel Name</Label>
                            <Input
                                id="channel-name"
                                value={newChannelName}
                                onChange={(e) => setNewChannelName(e.target.value)}
                                placeholder="e.g. general"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="channel-description">Description (optional)</Label>
                            <Input
                                id="channel-description"
                                value={newChannelDescription}
                                onChange={(e) => setNewChannelDescription(e.target.value)}
                                placeholder="What's this channel about?"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Group (optional)</Label>
                            <Select
                                value={selectedGroupForChannel?.toString() || 'none'}
                                onValueChange={(val) => setSelectedGroupForChannel(val === 'none' ? null : parseInt(val))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a group" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {customGroups.map(group => (
                                        <SelectItem key={group.id} value={group.id.toString()}>
                                            {group.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setCreateChannelOpen(false);
                            setNewChannelName('');
                            setNewChannelDescription('');
                            setSelectedGroupForChannel(null);
                        }}>
                            Cancel
                        </Button>
                        <Button onClick={createNewChannel}>Create Channel</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Group Dialog */}
            <Dialog open={createGroupOpen} onOpenChange={setCreateGroupOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New Group</DialogTitle>
                        <DialogDescription>
                            Groups help you organize your channels.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="group-name">Group Name</Label>
                            <Input
                                id="group-name"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                placeholder="e.g. Projects"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setCreateGroupOpen(false);
                            setNewGroupName('');
                        }}>
                            Cancel
                        </Button>
                        <Button onClick={createNewGroup}>Create Group</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rename Group Dialog */}
            <Dialog open={renameGroupDialogOpen} onOpenChange={setRenameGroupDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rename Group</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="rename-group">Group Name</Label>
                            <Input
                                id="rename-group"
                                value={renamingGroupName}
                                onChange={(e) => setRenamingGroupName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && updateGroupName()}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setRenameGroupDialogOpen(false);
                            setRenamingGroupId(null);
                            setRenamingGroupName('');
                        }}>
                            Cancel
                        </Button>
                        <Button onClick={updateGroupName}>Rename</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ChannelList;
